import { forwardRef } from 'react';
import type { MouseEvent } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseButtonProps } from './types';
import {
  getBaseButtonToneClassName,
  resolveBaseAppearance,
} from './theme';
import { cn, hasRenderableNode } from './utils';

const buttonSizeClassNames = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  lg: 'h-10 px-3.5 text-base',
} as const;

const iconOnlySizeClassNames = {
  sm: 'h-8 w-8 px-0',
  md: 'h-9 w-9 px-0',
  lg: 'h-10 w-10 px-0',
} as const;

const iconSizeClassNames = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

function nonEmptyText(value: string | undefined) {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function LoadingIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-full w-full motion-safe:animate-spin motion-reduce:animate-none"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        className="opacity-25"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3Z"
      />
    </svg>
  );
}

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(
  function BaseButton(
    {
      children,
      label,
      icon,
      endIcon,
      type = 'button',
      title,
      ariaLabel,
      tone,
      size,
      active = false,
      iconOnly = false,
      disabled = false,
      loading = false,
      stopPropagation = false,
      appearance,
      variant,
      density,
      state,
      className,
      classNames,
      style,
      onClick,
      'aria-label': nativeAriaLabel,
      ...buttonProps
    },
    ref,
  ) {
    for (const [name, value] of [
      ['active', active],
      ['iconOnly', iconOnly],
      ['disabled', disabled],
      ['loading', loading],
      ['stopPropagation', stopPropagation],
    ] as const) {
      if (typeof value !== 'boolean') {
        throw new TypeError(`BaseButton ${name} must be a boolean.`);
      }
    }
    if (size !== undefined && size !== 'sm' && size !== 'md' && size !== 'lg') {
      throw new TypeError('BaseButton size must be "sm", "md", or "lg".');
    }
    if (
      tone !== undefined &&
      tone !== 'default' &&
      tone !== 'primary' &&
      tone !== 'success' &&
      tone !== 'warning' &&
      tone !== 'danger' &&
      tone !== 'muted'
    ) {
      throw new TypeError(
        'BaseButton tone must be "default", "primary", "success", "warning", "danger", or "muted".',
      );
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance({
      ...resolvedAppearance,
      variant,
      density,
      state,
    });
    const resolvedLabel = children ?? label;
    const hasLabel =
      hasRenderableNode(resolvedLabel) &&
      (typeof resolvedLabel !== 'string' || Boolean(resolvedLabel.trim()));
    const explicitAriaLabel =
      nonEmptyText(ariaLabel) ?? nonEmptyText(nativeAriaLabel);
    const labelText =
      typeof resolvedLabel === 'string'
        ? nonEmptyText(resolvedLabel)
        : undefined;
    const resolvedTitle = nonEmptyText(title);
    if (iconOnly && !hasLabel && !explicitAriaLabel && !resolvedTitle) {
      throw new TypeError(
        'BaseButton iconOnly requires ariaLabel, aria-label, title, or a renderable label.',
      );
    }
    const resolvedAriaLabel =
      explicitAriaLabel ??
      labelText ??
      resolvedTitle;
    const resolvedDisabled = disabled || loading;
    const resolvedTone = tone ?? 'default';
    const resolvedState = state ?? (loading
      ? 'loading'
      : disabled
        ? 'disabled'
        : active
          ? 'active'
          : 'idle');

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (stopPropagation) {
        event.stopPropagation();
      }
      if (resolvedDisabled) return;
      onClick?.(event);
    };

    return (
      <button
        {...componentAttributes}
        {...buttonProps}
        ref={ref}
        type={type}
        title={title}
        aria-label={resolvedAriaLabel}
        aria-pressed={active || undefined}
        aria-busy={loading || undefined}
        data-variant={theme.variant}
        data-density={theme.density}
        disabled={resolvedDisabled}
        data-monkeys-component="button"
        data-slot="root"
        data-state={resolvedState}
        data-tone={resolvedTone}
        data-size={size}
        data-active={active || undefined}
        data-loading={loading || undefined}
        data-disabled={resolvedDisabled || undefined}
        data-icon-only={iconOnly || undefined}
        className={cn(
          theme.slots.buttonBase,
          iconOnly && theme.slots.buttonIconOnly,
          size && buttonSizeClassNames[size],
          iconOnly && size && iconOnlySizeClassNames[size],
          getBaseButtonToneClassName(theme, tone, active),
          resolvedDisabled && theme.slots.buttonDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.button,
          ...(active ? theme.styles.buttonActive : undefined),
          ...(resolvedDisabled ? theme.styles.buttonDisabled : undefined),
          ...style,
        }}
        onClick={handleClick}
      >
        {loading ? (
          <span
            data-slot="icon"
            data-state={resolvedState}
            data-tone={resolvedTone}
            data-size={size}
            className={cn(
              theme.slots.buttonIcon,
              size && iconSizeClassNames[size],
              classNames?.icon,
            )}
          >
            <LoadingIcon />
          </span>
        ) : icon ? (
          <span
            data-slot="icon"
            data-state={resolvedState}
            data-tone={resolvedTone}
            data-size={size}
            className={cn(
              theme.slots.buttonIcon,
              size && iconSizeClassNames[size],
              classNames?.icon,
            )}
          >
            {icon}
          </span>
        ) : null}
        {iconOnly ? (
          hasRenderableNode(resolvedLabel) ? (
            <span
              data-slot="label"
              data-state={resolvedState}
              data-tone={resolvedTone}
              data-size={size}
              className="sr-only"
            >
              {resolvedLabel}
            </span>
          ) : null
        ) : hasRenderableNode(resolvedLabel) ? (
          <span
            data-slot="label"
            data-state={resolvedState}
            data-tone={resolvedTone}
            data-size={size}
            className={cn(theme.slots.buttonLabel, classNames?.label)}
          >
            {resolvedLabel}
          </span>
        ) : null}
        {!loading && endIcon ? (
          <span
            data-slot="end-icon"
            data-state={resolvedState}
            data-tone={resolvedTone}
            data-size={size}
            className={cn(
              theme.slots.buttonIcon,
              size && iconSizeClassNames[size],
              classNames?.icon,
              classNames?.endIcon,
            )}
          >
            {endIcon}
          </span>
        ) : null}
      </button>
    );
  },
);
